import { useVoicesLanguages } from '@/hooks/useVoicesLanguages';
import GlobeColor from '@/images/GlobeColor.png';
import Globus from '@/images/Globus.svg';
import { ttsVoiceLanguageFilter } from '@/state/presenters';
import theme from '@/theme';
import styled from '@emotion/styled';
import { ExpandMore } from '@mui/icons-material';
import { ListSubheader } from '@mui/material';
import Select from '@mui/material/Select';
import { ReactElement, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import { FilterChip } from '../FilterChip';
import { FilterOption, FilterWithoutDividerOption } from '../FilterChipSelect';
import { SearchField } from './SearchField';
import { SelectItem } from './SelectItem';

const Flag = styled.img`
    width: 28px;
`;

const StyledSelect = styled(Select)`
    padding: 0 12px !important;
    border: 1px solid ${theme.colors.black[300]};
    border-radius: 50px !important;
    color: ${theme.colors.secondary[700]};
    max-width: 200px;

    :hover {
        border: 1px solid ${theme.colors.secondary[900]};
    }

    fieldset {
        border: none;
    }

    .MuiSelect-select {
        padding: 0 !important;
    }

    .MuiSvgIcon-root {
        color: ${theme.colors.secondary[900]};
    }
` as unknown as typeof Select;

const StyledOptionsList = styled.ul`
    height: 420px;
    overflow-x: hidden;
    overflow-y: auto;
`;

const defaultOption = { value: 'english', label: 'English', icon: Globus };
const multilingualOption = { value: 'multilingual', label: 'Multilingual', icon: <Flag src={GlobeColor} /> };
const allOption = { value: 'all', label: 'All', icon: <Flag src={GlobeColor} /> };

export const LanguageChipSelect = () => {
    const languages = useVoicesLanguages();
    const [search, setSearch] = useState('');
    const listRef = useRef<HTMLUListElement>(null);

    const [ttsVoiceLanguageFilterValue, setTtsVoiceLanguageFilter] = useRecoilState(ttsVoiceLanguageFilter);

    const handleChange = (option: FilterOption) => {
        setSearch('');
        setTtsVoiceLanguageFilter(option.value);
    };

    const handleClearSearch = (e: React.MouseEvent) => {
        e.stopPropagation();
        setSearch('');
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearch(e.target.value);

        if (listRef?.current) {
            listRef?.current?.scrollTo({ top: 0 });
        }
    };

    const options: FilterWithoutDividerOption[] = [
        allOption,
        multilingualOption,
        ...languages.map(option => ({
            value: option.locale,
            label: option.label,
            icon: option.flagSrc ? <Flag loading="lazy" src={option.flagSrc} alt={option.label} style={{ marginBottom: '2px' }} /> : null,
        })),
    ];

    const option = options.find(option => option.value === ttsVoiceLanguageFilterValue) || defaultOption;

    const filteredOptions = options.filter(option => option.label.toLowerCase().includes(search.toLowerCase()));

    const renderValue = (selected: unknown): ReactElement => {
        const selectedOption = options.find(option => option.value === selected);

        return <FilterChip option={selectedOption || option} />;
    };

    return (
        <StyledSelect
            data-testid="voice-language-filter"
            id="language-filter"
            MenuProps={{
                PaperProps: { sx: { borderRadius: '12px !important', overflow: 'hidden', width: '400px', height: '500px' } },
            }}
            value={option.value}
            renderValue={renderValue}
            IconComponent={ExpandMore}>
            <ListSubheader component="div" onKeyDown={(e: any) => e.stopPropagation()}>
                <SearchField search={search} handleSearchChange={handleSearchChange} handleClearSearch={handleClearSearch} />
            </ListSubheader>
            <StyledOptionsList ref={listRef}>
                {filteredOptions.map(o => (
                    <SelectItem option={o} onChange={handleChange} selected={o.value === ttsVoiceLanguageFilterValue} />
                ))}
            </StyledOptionsList>
        </StyledSelect>
    );
};
